<!--
  - Copyright (C) 2019. Archimedes Exhibitions GmbH,
  - Saarbrücker Str. 24, Berlin, Germany
  -
  - This file contains proprietary source code and confidential
  - information. Its contents may not be disclosed or distributed to
  - third parties unless prior specific permission by Archimedes
  - Exhibitions GmbH, Berlin, Germany is obtained in writing. This applies
  - to copies made in any form and using any medium. It applies to
  - partial as well as complete copies.
  -->

<template>
  <div>
    <!-- Secondary navigation -->
    <navbar-secondary>
      <template v-slot:items>
        <b-nav-item>
        </b-nav-item>
      </template>
    </navbar-secondary>
    <!-- BEGIN: content -->
    <b-container class="py-4" fluid>
      <!-- Errors -->
      <b-alert variant="danger" show v-if="clientError">-->
        <b>Client error:</b> {{ clientError }}
      </b-alert>
      <b-alert variant="danger" show v-if="driverError">
        <b>Driver error:</b> {{ driverError }}
      </b-alert>

      <div class="search-wrapper mb-5">
        <input type="text" v-model="searchQuery" ref="search"
               placeholder="Filter by name or type"/>
      </div>
      <!-- Table content -->
      <b-table striped hover :items="resultQuery" v-if="isDriverDataComplete"
               id="data-table" :per-page="perPage" :fields="tableFields"
               :current-page="currentPage">
        <template v-slot:cell(actions)="row">
          <b-button variant="dark" size="sm"
                    :to="{ name: 'clientCtrl', params: { client_id: row.value.id }}">
            <b-icon-play-fill></b-icon-play-fill> Ctrl
          </b-button>
        </template>
      </b-table>

      <div v-if="!resultQuery.length && !loading" class="text-center">
        Nothing found with this filter...
      </div>

      <b-pagination v-model="currentPage" :total-rows="rows" :per-page="perPage"
                    aria-controls="data-table" align="center" class="mt-5">
      </b-pagination>
    </b-container>
    <b-alert
      v-model="loading"
      class="position-fixed fixed-bottom m-0 rounded-0 text-center"
      style="z-index: 2000;"
      variant="primary">
      <b-icon-alarm></b-icon-alarm> Loading, please wait...
    </b-alert>
    <!-- END: content -->
  </div>
</template>

<script>
  export default {
    name: 'control',
    components: {
      NavbarSecondary: () => import('@/components/NavbarSecondary.vue')
    },

    data () {
      return {
        loading: false,
        clientError: null,
        driverError: null,
        clients: [],
        drivers: [],
        isDriverDataComplete: false,
        searchQuery: null,
        perPage: 20,
        currentPage: 1,
        tableFields: [
          {
            key: 'name',
            sortable: true
          },
          {
            key: 'info',
            sortable: true,
            thClass: 'd-none d-xl-table-cell',
            tdClass: 'd-none d-xl-table-cell'
          },
          {
            key: 'type',
            sortable: true,
            thClass: 'd-none d-lg-table-cell',
            tdClass: 'd-none d-lg-table-cell'
          },
          {
            key: 'actions',
            label: '',
            thClass: 'text-right',
            tdClass: 'text-right'
          }
        ],
        driverAliases: {}
      }
    },
    watch: {
      isDriverDataComplete () {
        this.$refs.search.focus()
      }
    },
    computed: {
      isDataComplete () {
        return this.isDriverDataComplete && this.clients.length
      },
      tableData () {
        let tableData = []
        this.clients.map(c => {
          tableData.push({
            name: c.clientName,
            info: this.getDriverById(c.driverId).info,
            type: this.getDriverAlias(c),
            actions: { id: c.clientId }
          })
        })
        return tableData
      },
      resultQuery () {
        if (this.searchQuery) {
          return this.tableData.filter(item => {
            return this.searchQuery
              .toLowerCase().split(' ')
              .every(v => item.name.toLowerCase().includes(v) ||
                item.type.toLowerCase().includes(v))
          })
        } else {
          return this.tableData
        }
      },
      rows () {
        return this.resultQuery.length
      }
    },

    methods: {
      updateClients (nextToken = null) {
        if (nextToken == null) {
          this.clients = []
          this.driverAliases = {}
          this.clientError = null
        }
        return this.$driverManager.listClients(nextToken)
          .then(async data => {
            let clients = []
            for (let d of data.clientsList) {
              let driver = this.getDriverById(d.driverId)
              let alias = await this.$driverManager.computeDriverAlias(d, driver)
              if (alias) {
                this.driverAliases[d.clientId] = alias
              }
              clients.push(d)
            }
            this.clients = Array.prototype.concat(this.clients, clients)
            if (data.nextPageToken) {
              return this.updateClients(data.nextPageToken)
            }
          })
          .catch((err) => {
            console.error(err)
            this.clientError = err
          })
      },

      updateDrivers (nextToken = null) {
        if (nextToken == null) {
          this.driverError = null
          this.drivers = []
          this.isDriverDataComplete = false
        }

        return this.$driverManager.listDrivers(nextToken)
          .then((data) => {
            let drivers = []
            for (let d of data.driversList) {
              drivers.push(d)
            }
            this.drivers = Array.prototype.concat(this.drivers, drivers)
            if (data.nextPageToken) {
              return this.updateDrivers(data.nextPageToken)
            } else {
              this.isDriverDataComplete = true
            }
          })
          .catch((err) => {
            this.driverError = err
          })
      },

      getDriverById (driverId) {
        return this.drivers.find(d => d.driverId === driverId)
      },

      getDriverAlias (client) {
        if (client.clientId in this.driverAliases) {
          return this.driverAliases[client.clientId]
        }
        return this.getDriverById(client.driverId).name
      }
    },

    created () {
      this.loading = true
      this.updateDrivers().then(() => {
        this.updateClients().then(() => {
          this.loading = false
        })
      })
    }
  }
</script>

<style scoped>
</style>
